import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import type { PlainMessage } from "@bufbuild/protobuf";

export type PlainGitHunk = PlainMessage<GitHunk>;

export const LINE_MARKER_ADDED = "+";
export const LINE_MARKER_REMOVED = "-";
export const LINE_MARKER_UNCHANGED = " ";

/**
 * Extracts the new file content from diff hunks by combining added and unchanged lines.
 * Note: This only returns the lines covered by the hunks, not the complete file.
 * For files with limited changes, this provides a reasonable preview.
 */
export const extractNewContentFromHunks = (hunks: PlainGitHunk[]): string => {
    const lines: string[] = [];
    for (const hunk of hunks) {
        const hunkLines = hunk.body.split("\n");
        for (let i = 0; i < hunkLines.length; i++) {
            const line = hunkLines[i];
            if (line === "" && i === hunkLines.length - 1) continue; // trailing newline
            // Empty mid-body lines are unchanged empty lines (diff.suppressBlankEmpty)
            const marker = line === "" ? LINE_MARKER_UNCHANGED : line[0];
            const content = line === "" ? "" : line.slice(1);
            // Include added lines and unchanged lines (skip removed lines)
            if (marker === LINE_MARKER_ADDED || marker === LINE_MARKER_UNCHANGED) {
                lines.push(content);
            }
        }
    }
    return lines.join("\n");
};

/**
 * Converts GitHunk[] to a unified diff patch string that can be parsed by @pierre/diffs.
 * The patch format follows the standard unified diff format:
 * --- a/filename
 * +++ b/filename
 * @@ -oldStart,oldLines +newStart,newLines @@ optional section
 * context/added/removed lines...
 */
export const convertHunksToPatch = (hunks: PlainGitHunk[], filePath: string): string => {
    if (hunks.length === 0) {
        return "";
    }

    // Use "diff --git" header so @pierre/diffs parses with GIT_DIFF_FILE_BREAK_REGEX,
    // avoiding false file-boundary matches when body lines start with "---".
    const lines: string[] = [`diff --git a/${filePath} b/${filePath}`, `--- a/${filePath}`, `+++ b/${filePath}`];

    for (const hunk of hunks) {
        let originalStartLine = hunk.originalStartLine;
        let originalLines = hunk.originalLines;
        let newStartLine = hunk.newStartLine;
        let newLines = hunk.newLines;

        // Add hunk body lines (already prefixed with +/-/space).
        // Empty strings arise from trailing newlines or when git's
        // diff.suppressBlankEmpty strips the leading space from empty
        // context lines. Treat them as unchanged empty lines (" ").
        const bodyLines: string[] = [];
        const hunkLines = hunk.body.split("\n");
        for (let i = 0; i < hunkLines.length; i++) {
            const line = hunkLines[i];
            if (line === "" && i === hunkLines.length - 1) {
                // Skip trailing empty string from a final newline
                continue;
            }
            bodyLines.push(line === "" ? " " : line);
        }

        // @pierre/diffs crashes on single-hunk patches where one side is empty
        // (pure addition or deletion) and startLine > 1. The renderer uses
        // absolute line numbers as indices into zero-based arrays. Prepend a
        // synthetic context line so both sides always have content.
        if (originalLines === 0 || newLines === 0) {
            bodyLines.unshift(" ");
            originalLines++;
            newLines++;
            originalStartLine = Math.max(1, originalStartLine - 1);
            newStartLine = Math.max(1, newStartLine - 1);
        }

        const header = `@@ -${originalStartLine},${originalLines} +${newStartLine},${newLines} @@${hunk.section ? ` ${hunk.section}` : ""}`;
        lines.push(header);
        lines.push(...bodyLines);
    }

    return lines.join("\n");
};

/**
 * Simple hash function for content comparison.
 * Uses a fast string hash algorithm (djb2).
 */
export const hashString = (str: string): string => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = (hash * 33) ^ str.charCodeAt(i);
    }
    return (hash >>> 0).toString(16);
};

/**
 * Extracts content for specific lines from hunks and computes a hash.
 * Used to detect if the code a comment refers to has changed.
 *
 * @param hunks - The diff hunks
 * @param startLine - Start line number (1-based, in the new file)
 * @param endLine - End line number (1-based, in the new file)
 * @param side - Which side of the diff ("additions" for new file, "deletions" for old file)
 * @returns Hash of the content for the specified lines, or null if lines not found
 */
export const computeContentHashForLines = (
    hunks: PlainGitHunk[],
    startLine: number,
    endLine: number,
    side: "additions" | "deletions" = "additions",
): string | null => {
    const lines: string[] = [];

    for (const hunk of hunks) {
        const hunkLines = hunk.body.split("\n");
        let currentNewLine = hunk.newStartLine;
        let currentOldLine = hunk.originalStartLine;

        for (let i = 0; i < hunkLines.length; i++) {
            const line = hunkLines[i];
            if (line === "" && i === hunkLines.length - 1) continue; // trailing newline
            // Empty mid-body lines are unchanged empty lines (diff.suppressBlankEmpty)
            const marker = line === "" ? LINE_MARKER_UNCHANGED : line[0];
            const content = line === "" ? "" : line.slice(1);

            if (side === "additions") {
                // Track new file line numbers
                // Note: Removed lines don't affect new file line numbers
                if (marker === LINE_MARKER_ADDED || marker === LINE_MARKER_UNCHANGED) {
                    if (currentNewLine >= startLine && currentNewLine <= endLine) {
                        lines.push(content);
                    }
                    currentNewLine++;
                }
            } else {
                // Track old file line numbers
                // Note: Added lines don't affect old file line numbers
                if (marker === LINE_MARKER_REMOVED || marker === LINE_MARKER_UNCHANGED) {
                    if (currentOldLine >= startLine && currentOldLine <= endLine) {
                        lines.push(content);
                    }
                    currentOldLine++;
                }
            }
        }
    }

    if (lines.length === 0) {
        return null;
    }

    return hashString(lines.join("\n"));
};

/**
 * Computes a hash for specific lines from raw file content.
 * Used to detect if the code a comment refers to has changed.
 *
 * @param content - The file content as a string
 * @param startLine - Start line number (1-based)
 * @param endLine - End line number (1-based)
 * @returns Hash of the content for the specified lines, or null if lines not found
 */
export const computeContentHash = (content: string, startLine: number, endLine: number): string | null => {
    if (content === "") {
        return null;
    }

    const allLines = content.split("\n");
    const selectedLines = allLines.slice(startLine - 1, endLine);

    if (selectedLines.length === 0) {
        return null;
    }

    return hashString(selectedLines.join("\n"));
};

export const isMdxFile = (path: string): boolean => path.toLowerCase().endsWith(".mdx");

export const isMarkdownFile = (path: string): boolean => {
    const lowerPath = path.toLowerCase();
    return lowerPath.endsWith(".md") || lowerPath.endsWith(".markdown") || isMdxFile(path);
};

export const isSvgFile = (path: string): boolean => path.toLowerCase().endsWith(".svg");

/**
 * Represents a range of lines in a diff hunk.
 */
export type HunkLineRange = {
    startLine: number;
    endLine: number;
};

/**
 * Checks if a line number falls within any of the diff hunk ranges.
 *
 * @param lineNumber - The line number to check (1-based)
 * @param hunkRanges - Array of hunk line ranges
 * @returns true if the line is within a hunk, false otherwise
 */
export const isLineInHunk = (lineNumber: number, hunkRanges: HunkLineRange[]): boolean => {
    return hunkRanges.some((range) => lineNumber >= range.startLine && lineNumber <= range.endLine);
};
